import { ifDefined } from 'lit/directives/if-defined.js';
import { ApplicationElement, html } from '@crossiety/lit-components';
import '../collage_item/element.js';

import style from './style.lit.css';

/**
 * * Presents collage up to 5 images. Collage with one slot children doesn't crop image.
 * * Collage layout is different if the first image is vertical or horizontal.
 * * Displays label with remain images count if total-count is bigger then images length.
 *
 * @prop totalCount - Total count of images in gallery (not in a collage)
 * @prop count - Internal property => Slot children count
 * @prop layout - Internal property => Different component style (options: horizontal, vertical)
 * @prop images - Internal property => Array of images inside slot
 */

export default class Collage extends ApplicationElement {
  static styles = [ApplicationElement.styles, style];

  static properties = {
    totalCount: { type: Number },
    count: { state: true },
    layout: { state: true },
    images: { state: true },
  };

  constructor() {
    super();
    this.layout = 'horizontal';
    this.images = [];
    /** @internal */
    this.limit = 4;
  }

  /** @internal */
  get countLeft() {
    return this.totalCount - this.count;
  }

  /** @internal */
  get shouldRenderLabel() {
    return this.totalCount > this.count;
  }

  getImageOrientation(img) {
    const w = img.width || img.naturalWidth;
    const h = img.height || img.naturalHeight;

    return h > w ? 'vertical' : 'horizontal';
  }

  handleSlotChange(e) {
    this.images = e.target
      .assignedElements({ flatten: true })
      .slice(0, this.limit);
    this.count = this.images.length;

    if (this.images[0].complete) {
      this.layout = this.getImageOrientation(this.images[0]);
      return;
    }

    this.images[0].onload = item => {
      this.layout = this.getImageOrientation(item.target);
    };
  }

  handleLabelClick() {
    this.shadowRoot.querySelector('xs-collage-item:last-of-type').click();
  }

  renderCollageItem(image) {
    return html`
      <xs-collage-item layout=${this.count === 1 ? 'static' : 'absolute'}>
        ${image}
      </xs-collage-item>
    `;
  }

  renderLabel() {
    return html`
      <button @click=${this.handleLabelClick}>+${this.countLeft}</button>
    `;
  }

  renderItems() {
    return this.images.map(image =>
      this.renderCollageItem(image.cloneNode(true))
    );
  }

  render() {
    return html`
      ${this.shouldRenderLabel ? this.renderLabel() : null}
      <main layout=${this.layout} count=${ifDefined(this.count)}>
        ${this.renderItems()}
      </main>
      <slot @slotchange=${this.handleSlotChange} hidden></slot>
    `;
  }
}

customElements.get('xs-collage') ||
  customElements.define('xs-collage', Collage);
